import mime from 'mime'
import { Fragment, VFC } from 'react'
import { Video } from '@opallabs/lapidary'
import { environment } from '@opallabs/principles'
import { assetProxyVideoUrls } from '@opallabs/routing'
import { DEV_UUID } from '@opallabs/starman'
import {
  useMediaControlsContext,
  useMediaStateContext,
} from '../../../kuiper/src/MediaProvider/context'
import { VideoProxyProps } from './types'

export const VideoProxy: VFC<VideoProxyProps> = ({
  assetReference,
  autoPlay = false,
  controls = false,
  preload = 'metadata',
  videoLoop = false,
}) => {
  const { onMetadataLoaded, onEnded, onPlay, setCurrentTime } =
    useMediaControlsContext()
  const { videoPlayFromTime, isPlaying, isMuted, volume } =
    useMediaStateContext()

  const transcodingUnavailable = environment.transcodingUnavailable()

  const {
    previewUrl,
    videoUrl: proxyVideoUrl,
    mp4Url = '',
    webmUrl = '',
  } = assetProxyVideoUrls({
    transcodingUnavailable,
    uuid: assetReference.uuid ?? '',
  })

  const videoUrl =
    assetReference.uuid === DEV_UUID ? assetReference.full_url : proxyVideoUrl

  return (
    <Video
      autoPlay={autoPlay}
      controls={controls}
      id={`video-${assetReference.uuid}`}
      isMuted={isMuted}
      isPlaying={isPlaying}
      onEnded={onEnded}
      onMetadataLoaded={onMetadataLoaded}
      onPlay={onPlay}
      poster={previewUrl}
      preload={preload}
      setCurrentTime={setCurrentTime}
      videoPlayFromTime={videoPlayFromTime}
      volume={volume}
      videoLoop={videoLoop}
    >
      {mp4Url ? <Video.Source src={mp4Url} type="video/mp4" /> : null}
      {webmUrl ? <Video.Source src={webmUrl} type="video/webm" /> : null}
      {videoUrl ? (
        <Fragment>
          {/* Attempting to load videos as mp4 first can work around
            some video types not supported by html5 <video> such as .mov */}
          <Video.Source src={videoUrl} type="video/mp4" />
          <Video.Source
            src={videoUrl}
            type={
              assetReference.format
                ? mime.getType(assetReference.format) || undefined
                : undefined
            }
          />
        </Fragment>
      ) : null}
    </Video>
  )
}
